import { XIcon } from "lucide-react";
import { useState } from "react";
import { Link, useMatch } from "react-router-dom";
import { openPortal } from "@/api/billing";
import { useAuth } from "@/auth/auth-provider";
import { Button } from "@/components/ui/button";
import { useCan } from "@/hooks/use-permissions";
import { openBillingDialog } from "./billing-events";
import { formatCredits } from "./format";
import { useCredits, useRedirectAction } from "./use-billing";

const DISMISS_KEY = "gridcast:low-credit-dismissed";

/**
 * Global banner above the page when credits run low, run out, or plan credits are frozen by a
 * failed payment. Dismissal lasts for the session and per severity.
 */
export function LowCreditBanner() {
	const { workspace } = useAuth();
	const credits = useCredits();
	const onBilling = !!useMatch("/settings/billing");
	const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY));
	const portal = useRedirectAction();
	const canManage = useCan("billing.manage");
	if (credits.level === "ok" || credits.spendable == null) return null;
	const key = `${workspace?.id}:${credits.level}`;
	if (dismissed === key && credits.level !== "past_due") return null;

	const copy =
		credits.level === "past_due"
			? {
					title: "Your last payment failed.",
					body: "Plan credits are paused until the card is updated. Pack credits still work.",
				}
			: credits.level === "empty"
				? {
						title: "You're out of credits.",
						body: "New generations, scrapes and paid publishes are paused.",
					}
				: {
						title: `Credits are running low — ${formatCredits(credits.spendable)} left.`,
						body: "Top up or upgrade so scheduled flows keep running.",
					};

	return (
		<div
			role="status"
			className="flex items-center gap-3 border-b bg-muted/60 px-6 py-2 text-[13px]"
		>
			<span className="size-1.5 shrink-0 rounded-full bg-destructive" />
			<p className="min-w-0 flex-1 truncate">
				<span className="font-medium">{copy.title}</span>{" "}
				<span className="text-muted-foreground">{copy.body}</span>
			</p>
			{canManage && credits.level === "past_due" ? (
				<Button
					size="sm"
					loading={portal.pending !== null}
					onClick={() => portal.run("pm", () => openPortal("payment_method_update"))}
				>
					Update card
				</Button>
			) : canManage ? (
				<Button size="sm" onClick={() => openBillingDialog("credits")}>
					Buy credits
				</Button>
			) : null}
			{!onBilling && (
				<Button asChild size="sm" variant="ghost">
					<Link to="/settings/billing">Usage</Link>
				</Button>
			)}
			{credits.level !== "past_due" && (
				<Button
					size="icon-sm"
					variant="ghost"
					aria-label="Dismiss"
					onClick={() => {
						sessionStorage.setItem(DISMISS_KEY, key);
						setDismissed(key);
					}}
				>
					<XIcon />
				</Button>
			)}
		</div>
	);
}
